import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  Input,
  Radio,
  Space,
  Spin,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { matchVendors, selectVendor, type SuggestedVendor } from "../../lib/api";
import { ditColors } from "../../lib/theme";
import type { IntakeWorkflow, VendorSelection } from "../intakeDetails/types";

interface Props {
  intakeId: string;
  workflow: IntakeWorkflow;
  onSelectionSaved: () => void;
}

const BUILD_INTERNAL_KEY = "__build_internal__";
const BAR_HEIGHT = 8;

export default function VendorMatchingStage({
  intakeId,
  workflow,
  onSelectionSaved,
}: Props) {
  const { message } = App.useApp();
  const [suggestions, setSuggestions] = useState<SuggestedVendor[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [running, setRunning] = useState(false);
  const [autoRunAttempted, setAutoRunAttempted] = useState(false);
  const [editing, setEditing] = useState<boolean>(!workflow.selectedVendor);
  const [selectedKey, setSelectedKey] = useState<string>(() => {
    const sel = workflow.selectedVendor;
    if (sel?.type === "vendor") return sel.vendorId;
    if (sel?.type === "build_internal") return BUILD_INTERNAL_KEY;
    return "";
  });
  const [reason, setReason] = useState<string>(
    workflow.selectedVendor?.type === "build_internal"
      ? workflow.selectedVendor.reason ?? ""
      : ""
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoaded(false);
    const unsub = onSnapshot(
      doc(db, "publicIntakes", intakeId),
      (snap) => {
        const data = snap.data();
        const next = Array.isArray(data?.suggestedVendors)
          ? (data!.suggestedVendors as SuggestedVendor[])
          : [];
        setSuggestions(next);
        setLoaded(true);
      },
      () => setLoaded(true)
    );
    return () => unsub();
  }, [intakeId]);

  useEffect(() => {
    if (!loaded || autoRunAttempted || suggestions.length > 0 || running) return;
    setAutoRunAttempted(true);
    void runMatch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, autoRunAttempted, suggestions.length, running]);

  useEffect(() => {
    setEditing(!workflow.selectedVendor);
    const sel = workflow.selectedVendor;
    if (sel?.type === "vendor") setSelectedKey(sel.vendorId);
    else if (sel?.type === "build_internal") setSelectedKey(BUILD_INTERNAL_KEY);
  }, [workflow.selectedVendor]);

  const sorted = useMemo(
    () => [...suggestions].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)),
    [suggestions]
  );

  const runMatch = async (silent = false) => {
    try {
      setRunning(true);
      const result = await matchVendors(intakeId);
      if (!silent) {
        message.success(
          `Found ${result.count} vendor match${result.count === 1 ? "" : "es"}.`
        );
      }
    } catch (error) {
      const text = error instanceof Error ? error.message : "Matching failed";
      if (!silent) message.error(text);
    } finally {
      setRunning(false);
    }
  };

  const handleSave = async () => {
    if (!selectedKey) {
      message.warning("Pick a vendor or 'Build internally' first.");
      return;
    }
    let decision;
    if (selectedKey === BUILD_INTERNAL_KEY) {
      decision = { type: "build_internal" as const, reason };
    } else {
      const vendor = sorted.find((v) => v.vendorId === selectedKey);
      if (!vendor) {
        message.error("Selected vendor not found in suggestions.");
        return;
      }
      decision = {
        type: "vendor" as const,
        vendorId: vendor.vendorId,
        vendorName: vendor.companyName,
      };
    }
    try {
      setSaving(true);
      await selectVendor({ intakeId, decision });
      message.success("Selection saved");
      setEditing(false);
      onSelectionSaved();
    } catch (error) {
      const text = error instanceof Error ? error.message : "Save failed";
      message.error(text);
    } finally {
      setSaving(false);
    }
  };

  const sel: VendorSelection | undefined = workflow.selectedVendor;

  if (!editing && sel) {
    const description =
      sel.type === "vendor"
        ? `Selected vendor: ${sel.vendorName}`
        : sel.reason
          ? `Build internally (NC DIT OAIP builds it) — ${sel.reason}`
          : "Build internally (NC DIT OAIP builds it)";
    return (
      <Card>
        <Alert
          type="success"
          showIcon
          message={description}
          action={
            <Button onClick={() => setEditing(true)}>Change selection</Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card
      title="Select Vendor"
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => runMatch(false)}
          loading={running}
          aria-label="Re-run vendor matching"
        >
          Re-run matching
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Pick the vendor you want to engage, or choose "Build internally" if
          NC DIT's OAIP (the AI Accelerator team) will build this in-house on
          behalf of the agency, rather than engaging an outside vendor. Your
          choice is required to advance to the AI Advisory Board.
        </Typography.Paragraph>

        {!loaded || (running && sorted.length === 0) ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <Spin tip="Generating vendor recommendations…" />
          </div>
        ) : (
          <Radio.Group
            value={selectedKey}
            onChange={(event) => setSelectedKey(event.target.value)}
            style={{ width: "100%" }}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Card size="small" type="inner">
                <Radio
                  value={BUILD_INTERNAL_KEY}
                  aria-describedby="build-internal-hint"
                >
                  <Typography.Text strong>Build internally</Typography.Text>
                </Radio>
                <div style={{ marginLeft: 24 }}>
                  <Typography.Text id="build-internal-hint" type="secondary">
                    NC DIT's OAIP (the AI Accelerator team) builds it in-house —
                    not the requesting agency.
                  </Typography.Text>
                </div>
                {selectedKey === BUILD_INTERNAL_KEY && (
                  <div style={{ marginTop: 8 }}>
                    <Typography.Text type="secondary">
                      Reason (optional):
                    </Typography.Text>
                    <Input.TextArea
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="Why is OAIP building this in-house instead of engaging a vendor?"
                      autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                  </div>
                )}
              </Card>

              {sorted.length === 0 ? (
                <Empty
                  description="No vendor matches yet. Click Re-run matching to generate suggestions."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                sorted.map((vendor) => {
                  const score = Math.max(0, Math.min(1, Number(vendor.score) || 0));
                  const widthPct = Math.round(score * 100);
                  return (
                    <Card
                      key={vendor.vendorId}
                      size="small"
                      type="inner"
                      title={
                        <Radio value={vendor.vendorId}>
                          <Typography.Text strong>
                            {vendor.companyName}
                          </Typography.Text>
                        </Radio>
                      }
                    >
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: "100%" }}
                      >
                        <div
                          role="progressbar"
                          aria-label={`Relevance for ${vendor.companyName}`}
                          aria-valuenow={widthPct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          style={{
                            width: "100%",
                            height: BAR_HEIGHT,
                            backgroundColor: ditColors.secondaryLight,
                            borderRadius: BAR_HEIGHT / 2,
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${widthPct}%`,
                              height: "100%",
                              backgroundColor: ditColors.primary,
                              transition: "width 200ms ease",
                            }}
                          />
                        </div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Relevance: {widthPct}%
                        </Typography.Text>
                        {vendor.explanation && (
                          <Typography.Paragraph style={{ marginBottom: 0 }}>
                            {vendor.explanation}
                          </Typography.Paragraph>
                        )}
                      </Space>
                    </Card>
                  );
                })
              )}
            </Space>
          </Radio.Group>
        )}

        <Space>
          <Button
            type="primary"
            loading={saving}
            disabled={!selectedKey}
            onClick={handleSave}
          >
            Save selection
          </Button>
          {sel && (
            <Button onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  );
}
